import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import RadioButton from '../../ui/buttons/RadioButton';
import '../../styles/components/prompt-builder/CategorySelector.scss';

interface CategorySelectorProps {
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
}

const CATEGORIES = [
  {
    id: 'performance',
    name: 'Performance Overview',
    description: 'Activity, visibility, traffic, and efficiency',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M22 12h-4l-3 9L9 3l-3 9H2' />
      </svg>
    ),
    tooltip: 'Impressions, clicks, page views, sessions, and ad spend',
  },
  {
    id: 'ad_performance',
    name: 'Ad Performance',
    description: 'Ad effectiveness, campaign metrics, and ROI',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <rect width='18' height='18' x='3' y='3' rx='2' />
        <path d='M9 15V9l7 3-7 3Z' />
      </svg>
    ),
    tooltip:
      'CTR, CPC, impressions, ad spend, ROAS, campaign performance, and ad engagement',
  },
  {
    id: 'conversions',
    name: 'Conversions & Sales',
    description: 'Sales, transactions, leads, checkout completions',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M3 3v18h18' />
        <path d='m19 9-5 5-4-4-3 3' />
      </svg>
    ),
    tooltip:
      'Purchases, conversions, add-to-cart events, checkouts, and completed goals',
  },
  {
    id: 'revenue',
    name: 'Revenue & Financial Overview',
    description: 'Financial performance, profitability, customer value',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <circle cx='12' cy='12' r='10' />
        <path d='M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8' />
        <path d='M12 18V6' />
      </svg>
    ),
    tooltip:
      'Revenue, refunds, net sales, ROAS, average order value, customer lifetime value',
  },
  {
    id: 'audience',
    name: 'Audience & Demographics',
    description: 'Audience profiles and segments',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
        <circle cx='9' cy='7' r='4' />
        <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
        <path d='M16 3.13a4 4 0 0 1 0 7.75' />
      </svg>
    ),
    tooltip: 'Demographics, devices, locations, age, gender, and interests',
  },
  {
    id: 'funnel',
    name: 'Sales Funnel & Checkout Journey',
    description: 'Funnel analysis, drop-offs, conversion paths',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M22 2H2v5l8 8v7l4-4v-3l8-8V2z' />
      </svg>
    ),
    tooltip: 'Journey from product views to checkout to completed payments',
  },
  {
    id: 'engagement',
    name: 'Engagement & Interactions',
    description: 'User interactions, content engagement, conversations',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v5Z' />
        <path d='M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1' />
      </svg>
    ),
    tooltip:
      'Likes, comments, shares, chat interactions, email replies, and social engagement',
  },
  {
    id: 'leads',
    name: 'Leads & Contacts Management',
    description: 'Lead capture, contact growth, prospecting',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' />
        <path d='M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z' />
        <path d='M12 11h4' />
        <path d='M12 16h4' />
        <path d='M8 11h.01' />
        <path d='M8 16h.01' />
      </svg>
    ),
    tooltip: 'Contact forms, CRM entries, lead generation, and email sign-ups',
  },
  {
    id: 'attribution',
    name: 'Attribution & Multi-Channel Impact',
    description: 'Attribution modeling, channel performance, conversion paths',
    icon: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='category-icon'
      >
        <path d='M18 20a6 6 0 0 0-12 0' />
        <circle cx='12' cy='10' r='1' />
        <path d='M9 6a3 3 0 1 1 6 0' />
        <path d='M12 18v-3' />
        <path d='M13 15h-2' />
      </svg>
    ),
    tooltip:
      'Assisted conversions, cross-platform impact, and customer journeys across multiple channels',
  },
];

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategories,
  onChange,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const handleCategoryChange = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      onChange(selectedCategories.filter((c) => c !== categoryId));
    } else {
      onChange([...selectedCategories, categoryId]);
    }
  };

  const toggleExpand = (categoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(
        expandedCategories.filter((id) => id !== categoryId)
      );
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);

  return (
    <div className='category-selector'>
      {CATEGORIES.map((category) => {
        const isExpanded = expandedCategories.includes(category.id);
        const isSelected = selectedCategories.includes(category.id);
        return (
          <div
            key={category.id}
            className={`category-selector__item ${
              isSelected ? 'selected' : ''
            }`}
            style={
              isDarkMode
                ? {
                    backgroundColor: isSelected ? '#f59e0b' : '#374151',
                    borderColor: isSelected ? '#f59e0b' : '#4b5563',
                    color: isSelected ? '#000' : '#e5e7eb',
                  }
                : undefined
            }
            onClick={() => handleCategoryChange(category.id)}
          >
            <div className='category-selector__item-header'>
              <div className='category-selector__item-content'>
                <RadioButton
                  label=''
                  name={`category-${category.id}`}
                  value={category.id}
                  checked={isSelected}
                  onChange={() => handleCategoryChange(category.id)}
                />
                <div className='category-selector__item-info'>
                  <div
                    className='category-selector__item-icon'
                    style={
                      isDarkMode
                        ? { color: isSelected ? '#000' : '#e5e7eb' }
                        : undefined
                    }
                  >
                    {category.icon}
                  </div>
                  <span
                    className='category-selector__item-name'
                    style={
                      isDarkMode
                        ? { color: isSelected ? '#000' : '#e5e7eb' }
                        : undefined
                    }
                  >
                    {category.name}
                  </span>
                </div>
                <button
                  type='button'
                  onClick={(e) => toggleExpand(category.id, e)}
                  className='category-selector__expand-button'
                  style={
                    isDarkMode
                      ? { color: isSelected ? '#000' : '#e5e7eb' }
                      : undefined
                  }
                  aria-label={
                    isExpanded ? 'Collapse details' : 'Expand details'
                  }
                >
                  {isExpanded ? '−' : '+'}
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className='category-selector__item-details'>
                <p className='category-selector__item-description'>
                  {category.description}
                </p>
                <p className='category-selector__item-tooltip'>
                  {category.tooltip}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
